import { useState, useEffect, useCallback } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface SystemMetrics {
  activeAgents: number;
  totalAgents: number;
  totalConversations: number;
  activeConversations: number;
  messagesPerHour: number;
  auditEntriesTotal: number;
  memory: {
    heapUsedMB: number;
    heapTotalMB: number;
    rssMB: number;
    externalMB: number;
  };
  uptime: {
    seconds: number;
    formatted: string;
  };
  timestamp: string;
}

interface ProviderStatus {
  name: string;
  status: "healthy" | "degraded" | "down";
  latencyMs: number;
  errorRate: number;
  lastChecked: string;
  model: string;
}

interface AuditEntry {
  id: string;
  timestamp: string;
  actorId: string;
  actorType: string;
  action: string;
  status: string;
}

// ── API helpers ──────────────────────────────────────────────────────────────

const BASE = "/api";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("flax_token") ?? sessionStorage.getItem("flax_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function fetchMetrics(): Promise<SystemMetrics> {
  const res = await fetch(`${BASE}/admin/metrics`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch metrics: ${res.status}`);
  return res.json();
}

async function fetchProviders(): Promise<ProviderStatus[]> {
  const res = await fetch(`${BASE}/admin/providers/health`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch providers: ${res.status}`);
  const data = await res.json();
  return data.providers;
}

// ── Component ────────────────────────────────────────────────────────────────

export function AdminDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>("");

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [metricsData, providerData] = await Promise.all([
        fetchMetrics(),
        fetchProviders(),
      ]);
      setMetrics(metricsData);
      setProviders(providerData);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30_000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => {
    setLoading(true);
    loadData();
  };

  if (loading && !metrics) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="admin-loading">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
          <p className="mt-3 text-sm text-ink-500">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="admin-error">
        <div className="text-center">
          <p className="text-sm font-medium text-red-600">Error loading dashboard</p>
          <p className="mt-1 text-xs text-ink-500">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-3 rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Admin Dashboard</h1>
          <p className="text-sm text-ink-500">
            System overview and monitoring
            {lastRefresh && <span className="ml-2">-- Last updated: {lastRefresh}</span>}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-2 disabled:opacity-50"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          {error}
        </div>
      )}

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="metrics-cards">
          <MetricCard
            title="Active Agents"
            value={metrics.activeAgents}
            subtitle={`${metrics.totalAgents} total`}
            indicator="up"
          />
          <MetricCard
            title="Conversations"
            value={metrics.totalConversations}
            subtitle={`${metrics.activeConversations} active`}
            indicator={metrics.activeConversations > 0 ? "up" : "neutral"}
          />
          <MetricCard
            title="Uptime"
            value={metrics.uptime.formatted}
            subtitle="Server uptime"
            indicator="up"
          />
          <MetricCard
            title="Memory"
            value={`${metrics.memory.heapUsedMB} MB`}
            subtitle={`${metrics.memory.heapTotalMB} MB total heap`}
            indicator={
              metrics.memory.heapUsedMB / metrics.memory.heapTotalMB > 0.8
                ? "down"
                : "neutral"
            }
          />
        </div>
      )}

      {/* Secondary Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="secondary-metrics">
          <MetricCard
            title="Messages/Hour"
            value={metrics.messagesPerHour}
            subtitle="Throughput"
            indicator={metrics.messagesPerHour > 0 ? "up" : "neutral"}
          />
          <MetricCard
            title="Audit Entries"
            value={metrics.auditEntriesTotal}
            subtitle="Total logged"
            indicator="neutral"
          />
          <MetricCard
            title="RSS Memory"
            value={`${metrics.memory.rssMB} MB`}
            subtitle="Process resident set"
            indicator="neutral"
          />
        </div>
      )}

      {/* Provider Health */}
      <div data-testid="provider-health">
        <h2 className="mb-3 text-lg font-semibold text-ink-800">Provider Health</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {providers.map((provider) => (
            <ProviderCard key={provider.name} provider={provider} />
          ))}
          {providers.length === 0 && (
            <p className="col-span-full text-sm text-ink-400">No providers configured</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {metrics && (
        <div data-testid="quick-stats">
          <h2 className="mb-3 text-lg font-semibold text-ink-800">System Info</h2>
          <div className="rounded-lg border border-ink-100 bg-white p-4">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <dt className="text-xs text-ink-500">Heap Used</dt>
                <dd className="text-sm font-medium text-ink-900">{metrics.memory.heapUsedMB} MB</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-500">Heap Total</dt>
                <dd className="text-sm font-medium text-ink-900">{metrics.memory.heapTotalMB} MB</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-500">External Memory</dt>
                <dd className="text-sm font-medium text-ink-900">{metrics.memory.externalMB} MB</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-500">Uptime (seconds)</dt>
                <dd className="text-sm font-medium text-ink-900">{metrics.uptime.seconds.toLocaleString()}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function MetricCard({
  title,
  value,
  subtitle,
  indicator,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  indicator: "up" | "down" | "neutral";
}) {
  const indicatorColor =
    indicator === "up"
      ? "text-emerald-600"
      : indicator === "down"
        ? "text-red-600"
        : "text-ink-400";

  const indicatorSymbol =
    indicator === "up" ? "^" : indicator === "down" ? "v" : "-";

  return (
    <div className="rounded-lg border border-ink-100 bg-white p-4" data-testid="metric-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-ink-900">{value}</p>
        </div>
        <span className={`text-sm font-bold ${indicatorColor}`}>{indicatorSymbol}</span>
      </div>
      <p className="mt-1 text-xs text-ink-400">{subtitle}</p>
    </div>
  );
}

function ProviderCard({ provider }: { provider: ProviderStatus }) {
  const statusColors = {
    healthy: { bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-700" },
    degraded: { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", text: "text-amber-700" },
    down: { bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500", text: "text-red-700" },
  };

  const colors = statusColors[provider.status];

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-3`} data-testid="provider-card">
      <div className="flex items-center gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${colors.dot}`} />
        <span className="text-sm font-semibold capitalize text-ink-800">{provider.name}</span>
      </div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-ink-500">Status</span>
          <span className={`font-medium ${colors.text}`}>{provider.status}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ink-500">Latency</span>
          <span className="font-medium text-ink-700">
            {provider.latencyMs >= 0 ? `${provider.latencyMs}ms` : "N/A"}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ink-500">Error Rate</span>
          <span className="font-medium text-ink-700">{(provider.errorRate * 100).toFixed(1)}%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-ink-500">Model</span>
          <span className="truncate font-medium text-ink-700">{provider.model}</span>
        </div>
      </div>
    </div>
  );
}
